'use client';

import { z as zod } from 'zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';

import { paths, newSearchParams } from 'src/routes/paths';
import { useRouter, useSearchParams } from 'src/routes/hooks';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';
import { Form, Field } from 'src/components/hook-form';

import { getErrorMessage } from '../../utils';
import { sendPasswordResetEmail } from '../../context/firebase';
import { FormCard, FormHead, FormReturnLink } from '../../components';

// ----------------------------------------------------------------------

const ResetPasswordSchema = (t) =>
  zod.object({
    email: zod
      .string()
      .min(1, { message: t('Email required') })
      .email({ message: t('Invalid email') }),
  });

const defaultValues = { email: '' };

export function FirebaseResetPasswordView() {
  const router = useRouter();

  const searchParams = useSearchParams();

  const { t, currentLang } = useTranslate();

  const [errorMsg, setErrorMsg] = useState('');

  const methods = useForm({
    resolver: zodResolver(ResetPasswordSchema(t)),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      setErrorMsg('');
      await sendPasswordResetEmail({ email: data.email });
      router.push(
        `${paths.auth.firebase.verify}${newSearchParams({
          country: currentLang.countryName,
          email: data.email,
        })}`
      );
    } catch (error) {
      setErrorMsg(getErrorMessage(error));
    }
  });

  const renderError = () => (
    <Alert severity="error" onClose={() => setErrorMsg('')}>
      {t(errorMsg)}.
    </Alert>
  );

  const renderForm = () => (
    <Box gap={3} display="flex" flexDirection="column">
      {!!errorMsg && renderError()}

      <Field.Text
        autoFocus
        name="email"
        label={t('Email')}
        placeholder="example@domain.com"
        slotProps={{ inputLabel: { shrink: true } }}
      />

      <Button
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        loadingIndicator={`${t('Sending request')}...`}
      >
        {t('Send request')}
      </Button>
    </Box>
  );

  return (
    <FormCard>
      <FormHead
        icon={<Iconify icon="fluent-color:person-key-24" width={80} />}
        title={t('Forgot password?')}
        description={t(
          'Please enter the email address associated with your account and We will email you a link to reset your password'
        )}
        slotProps={{ description: { sx: { mb: 1.5 } } }}
      />

      <Form methods={methods} onSubmit={onSubmit}>
        {renderForm()}
      </Form>

      <FormReturnLink
        href={`${paths.auth.firebase.signIn}${newSearchParams({ ...Object.fromEntries(searchParams), country: currentLang.countryName })}`}
      />
    </FormCard>
  );
}
